import { useMemo } from "react";
import {
  calculateSymbolStats,
  calculateMonthlyStats,
  calculateCumulativePnl,
  calculatePnlDistribution,
  calculateRollingWinRate,
  calculateHourDayHeatmap,
  calculateTimeSlotStats,
  calculateHoldDurationData,
  calculateTagStats,
  calculateBehavioralBiases,
  calculateSessionStats,
  calculatePipStats,
  calculatePipBuckets,
  calculatePipsBySymbol,
  calculateCalmarRatio,
  calculateRecoveryFactor,
  calculateSizingData,
  calculateLongShortStats,
  calculateDayOfWeekStats,
  calculateStreakData,
  calculateConsistencyScore,
  calculateNetDailyPnl,
  calculateDrawdownCurve,
} from "../../trading-journal/analytics";
import type { AnalyticsMetric } from "./registry";

/**
 * Calcul analytics à la carte.
 *
 * Chaque métrique est exposée via un getter qui calcule à la PREMIÈRE lecture
 * puis mémorise le résultat. Un widget qui lit `analyticsData.symbolStats` ne
 * déclenche que `calculateSymbolStats` — les 22 autres passes ne s'exécutent
 * jamais si personne ne les lit.
 *
 * Pourquoi des getters plutôt qu'un objet construit à partir d'un Set de
 * métriques : l'API publique reste strictement identique (`analyticsData.x`),
 * donc aucun widget n'a eu à changer, et il est IMPOSSIBLE qu'un widget lise une
 * métrique non calculée — le getter la produit à la demande. Le Set `metrics`
 * n'est qu'une optimisation supplémentaire (voir plus bas), pas une condition de
 * correction.
 *
 * Les getters sont `enumerable: false` : un `{...analyticsData}` ou un
 * `JSON.stringify` déclencherait sinon les 23 calculs d'un coup, ce qui
 * réintroduirait exactement le problème qu'on corrige.
 */

/** Le contrat de données consommé par les widgets analytics. */
export interface AnalyticsData {
  symbolStats: ReturnType<typeof calculateSymbolStats>;
  monthlyStats: ReturnType<typeof calculateMonthlyStats>;
  cumulativePnl: Array<{ x: number; y: number }>;
  pnlDistribution: ReturnType<typeof calculatePnlDistribution>;
  rollingWinRate: ReturnType<typeof calculateRollingWinRate>;
  heatmapData: ReturnType<typeof calculateHourDayHeatmap>;
  timeSlotStats: ReturnType<typeof calculateTimeSlotStats>;
  holdDurationData: ReturnType<typeof calculateHoldDurationData>;
  tagStats: ReturnType<typeof calculateTagStats>;
  behavioralBiases: ReturnType<typeof calculateBehavioralBiases>;
  sessionStats: ReturnType<typeof calculateSessionStats>;
  pipStats: ReturnType<typeof calculatePipStats>;
  pipBuckets: ReturnType<typeof calculatePipBuckets>;
  pipBySymbol: ReturnType<typeof calculatePipsBySymbol>;
  calmarRatio: ReturnType<typeof calculateCalmarRatio>;
  recoveryFactor: ReturnType<typeof calculateRecoveryFactor>;
  sizingData: ReturnType<typeof calculateSizingData>;
  longShort: ReturnType<typeof calculateLongShortStats>;
  dayOfWeek: ReturnType<typeof calculateDayOfWeekStats>;
  streakData: ReturnType<typeof calculateStreakData>;
  consistencyScore: ReturnType<typeof calculateConsistencyScore>;
  netDailyPnl: ReturnType<typeof calculateNetDailyPnl>;
  drawdownCurve: ReturnType<typeof calculateDrawdownCurve>;
}

/**
 * Construit l'objet analytics paresseux. Exporté pour être testable hors React.
 *
 * @param metrics Si fourni, seules ces métriques sont exposées ; les autres
 *   renvoient `undefined` sans jamais être calculées. Sert de filet de sécurité
 *   pour qu'un widget hors-scope ne puisse pas déclencher une passe coûteuse.
 *   Si omis, toutes les métriques restent disponibles à la demande.
 * @param onCompute Hook de test : appelé avec le nom de la métrique lors d'un
 *   calcul réel (jamais lors d'une lecture mémorisée).
 */
export function createLazyAnalyticsData(
  trades: any[],
  sessions: any[],
  metrics?: ReadonlySet<AnalyticsMetric>,
  onCompute?: (metric: AnalyticsMetric) => void,
): AnalyticsData {
  const cache = new Map<AnalyticsMetric, unknown>();

  function compute<T>(metric: AnalyticsMetric, fn: () => T): T {
    if (cache.has(metric)) return cache.get(metric) as T;
    onCompute?.(metric);
    const value = fn();
    cache.set(metric, value);
    return value;
  }

  const target = {} as AnalyticsData;

  function defineMetric<K extends AnalyticsMetric>(
    metric: K,
    fn: () => unknown,
  ): void {
    Object.defineProperty(target, metric, {
      // Non énumérable : protège contre un spread/stringify qui forcerait
      // l'évaluation de toutes les métriques d'un coup.
      enumerable: false,
      configurable: true,
      get: () => {
        // Métrique hors du périmètre demandé : ne pas calculer.
        if (metrics && !metrics.has(metric)) return undefined;
        return compute(metric, fn);
      },
    });
  }

  defineMetric("symbolStats", () => calculateSymbolStats(trades));
  defineMetric("monthlyStats", () => calculateMonthlyStats(trades));
  defineMetric("cumulativePnl", () =>
    calculateCumulativePnl(trades).map((p) => ({
      x: new Date(p.date).getTime(),
      y: p.cumulativePnl,
    })),
  );
  defineMetric("pnlDistribution", () => calculatePnlDistribution(trades));
  defineMetric("rollingWinRate", () => calculateRollingWinRate(trades));
  defineMetric("heatmapData", () => calculateHourDayHeatmap(trades));
  defineMetric("timeSlotStats", () => calculateTimeSlotStats(trades));
  defineMetric("holdDurationData", () => calculateHoldDurationData(trades as any));
  defineMetric("tagStats", () => calculateTagStats(trades as any));
  defineMetric("behavioralBiases", () => calculateBehavioralBiases(trades as any));
  defineMetric("sessionStats", () => calculateSessionStats(sessions ?? [], trades));
  defineMetric("pipStats", () => calculatePipStats(trades as any));
  defineMetric("pipBuckets", () => calculatePipBuckets(trades as any));
  defineMetric("pipBySymbol", () => calculatePipsBySymbol(trades as any));
  defineMetric("calmarRatio", () => calculateCalmarRatio(trades as any));
  defineMetric("recoveryFactor", () => calculateRecoveryFactor(trades as any));
  defineMetric("sizingData", () => calculateSizingData(trades));
  defineMetric("longShort", () => calculateLongShortStats(trades));
  defineMetric("dayOfWeek", () => calculateDayOfWeekStats(trades));
  defineMetric("streakData", () => calculateStreakData(trades));
  defineMetric("netDailyPnl", () => calculateNetDailyPnl(trades));
  defineMetric("drawdownCurve", () => calculateDrawdownCurve(trades));

  // consistencyScore dépend de streakData. On passe par `compute` directement
  // (et non par le getter public) pour que la dépendance soit satisfaite même si
  // "streakData" n'est pas dans le périmètre demandé, tout en profitant du cache
  // si les deux sont lues.
  defineMetric("consistencyScore", () =>
    calculateConsistencyScore(
      trades,
      compute("streakData", () => calculateStreakData(trades)),
    ),
  );

  return target;
}

/**
 * Expose les données analytics pour les widgets d'un layout.
 *
 * @param metrics Périmètre de calcul — typiquement `metricsForWidgetKeys(...)`
 *   à partir des widgets réellement présents. Omis = toutes les métriques
 *   disponibles à la demande (comportement historique, mais toujours paresseux).
 */
export function useAnalyticsData(
  trades: any[],
  sessions: any[],
  enabled: boolean = true,
  metrics?: ReadonlySet<AnalyticsMetric>,
): AnalyticsData | undefined {
  return useMemo(() => {
    if (!enabled || trades.length === 0) return undefined;
    return createLazyAnalyticsData(trades, sessions, metrics);
  }, [enabled, trades, sessions, metrics]);
}
